import { ContentLoading } from "../components/ContentLoading";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { History, RotateCcw } from "lucide-react";
import { useState } from "react";
import { documentMarkdown } from "../knowledge/document-markdown";
import type { OpsDocPage } from "../../shared/operations-docs-schemas";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { FieldError } from "../components/ui/field";
import { DocumentBody } from "../knowledge/DocumentBody";
import { getOpsDocHistory, getOpsDocRevision, updateOpsDocsPage } from "./docs-api";

function useRestoreVersion({
  page,
  version,
  onRestored,
}: {
  page: OpsDocPage;
  version: Awaited<ReturnType<typeof getOpsDocRevision>> | undefined;
  onRestored: () => void;
}) {
  const client = useQueryClient();
  const restore = useMutation({
    mutationFn: async () => {
      if (!version) throw new Error("Select a version first.");
      return updateOpsDocsPage(page.id, {
        expectedRevision: page.revision,
        title: version.revision.title,
        blocks: version.revision.blocks,
        links: page.links.map(({ entityType, entityId }) => ({ entityType, entityId })),
      });
    },
    onSuccess: async (response) => {
      client.setQueryData(["ops", "docs", "page", page.id], response);
      await client.invalidateQueries({ queryKey: ["ops", "docs", "bootstrap"] });
      onRestored();
    },
  });
  return restore;
}

function useHistory(page: OpsDocPage) {
  return useInfiniteQuery({
    queryKey: ["ops", "docs", "history", page.id, page.revision],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => getOpsDocHistory(page.id, pageParam),
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });
}

function VersionList({
  history,
  selected,
  page,
  onSelect,
}: {
  history: ReturnType<typeof useHistory>;
  selected: number;
  page: OpsDocPage;
  onSelect: (revision: number) => void;
}) {
  return (
    <nav
      aria-label="Document versions"
      className="max-h-48 overflow-y-auto border-b bg-muted/30 p-2 sm:max-h-none sm:border-r sm:border-b-0"
    >
      {history.isPending ? <ContentLoading label="Loading history…" className="p-3" /> : null}
      {history.error ? <FieldError>{history.error.message}</FieldError> : null}
      {history.data?.pages
        .flatMap((batch) => batch.revisions)
        .map((revision) => (
          <button
            key={revision.revision}
            type="button"
            aria-current={selected === revision.revision ? "true" : undefined}
            onClick={() => {
              onSelect(revision.revision);
            }}
            className={`w-full rounded-sm px-3 py-3 text-left text-sm hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring ${selected === revision.revision ? "bg-accent" : ""}`}
          >
            <span className="block font-semibold">
              Version {revision.revision}
              {revision.revision === page.revision ? " · Current" : ""}
            </span>
            <span className="block text-xs text-muted-foreground">
              {revision.author} · {new Date(revision.createdAt * 1000).toLocaleString()}
            </span>
          </button>
        ))}
      {history.hasNextPage ? (
        <Button
          variant="ghost"
          disabled={history.isFetchingNextPage}
          onClick={() => {
            void history.fetchNextPage();
          }}
        >
          Older versions
        </Button>
      ) : null}
    </nav>
  );
}

function HistoryContent({
  page,
  canRestore,
  onRestored,
}: {
  page: OpsDocPage;
  canRestore: boolean;
  onRestored: () => void;
}) {
  const [selected, setSelected] = useState(page.revision);
  const history = useHistory(page);
  const version = useQuery({
    queryKey: ["ops", "docs", "revision", page.id, selected],
    queryFn: () => getOpsDocRevision(page.id, selected),
  });
  const restore = useRestoreVersion({ page, version: version.data, onRestored });
  return (
    <>
      <DialogBody>
        <div className="grid h-[60dvh] min-h-80 grid-cols-1 overflow-hidden rounded-md border sm:grid-cols-[210px_minmax(0,1fr)]">
          <VersionList history={history} selected={selected} page={page} onSelect={setSelected} />

          <div className="overflow-y-auto p-5 sm:p-8">
            {version.isPending ? (
              <ContentLoading label="Loading version…" variant="document" />
            ) : null}
            {version.error ? <FieldError>{version.error.message}</FieldError> : null}
            {version.data ? (
              <>
                <h2 className="mb-6 text-2xl font-semibold">{version.data.revision.title}</h2>
                <DocumentBody
                  markdown={documentMarkdown(version.data.revision.blocks)}
                  pageId={page.id}
                />
              </>
            ) : null}
          </div>
        </div>
        {restore.error ? <FieldError>{restore.error.message}</FieldError> : null}
      </DialogBody>
      {canRestore ? (
        <DialogFooter
          start={
            <p className="m-0 max-w-md text-sm text-muted-foreground">
              Restore creates a new version. Current sharing and linked records stay in place.
            </p>
          }
        >
          <Button
            disabled={selected === page.revision || !version.data || restore.isPending}
            onClick={() => {
              restore.mutate();
            }}
          >
            <RotateCcw />
            {restore.isPending ? "Restoring…" : "Restore this version"}
          </Button>
        </DialogFooter>
      ) : (
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />}>Done</DialogClose>
        </DialogFooter>
      )}
    </>
  );
}

export function DocsHistoryDialog({ page, canRestore }: { page: OpsDocPage; canRestore: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="ghost" />}>
        <History />
        History
      </DialogTrigger>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
          <DialogDescription>Review previous versions of {page.title}.</DialogDescription>
        </DialogHeader>
        {open ? (
          <HistoryContent
            page={page}
            canRestore={canRestore}
            onRestored={() => {
              setOpen(false);
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
